import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { makeStyles, Box, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Alert from "@material-ui/lab/Alert";
import Grid from "@material-ui/core/Grid";
import LoaderView from "../../../components/LoaderView";
import ApexCharts from "react-apexcharts";
import { actGrpStackColorMap } from '../../../config/index';
import { getEmployeeWeekData } from "utils/report";
import { getWeekNumber } from "services";
import { isEmptyObject } from "utils";
import {
  getTranslatedActivityGroupFromCamelCase,
  getTranslatedJobFamilyFromCamelCase,
} from "../components/utils";
import _ from 'lodash';

const useStyles = makeStyles({});

const EmployeesActivities = (props) => {
  const { employeesUnderManager, employeesUnderManagerLoading } = props,
    { t } = useTranslation(),
    classes = useStyles();
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerJobFamilyHours, setWorkerJobFamilyHours] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [workerActivityGroupHours, setWorkerActivityGroupHours] =
    useState(null);
  const [workerHoursLoading, setWorkerHoursLoading] = useState(true);
  const [isJobFamilyDataAvailable, setIsJobFamilyDataAvailable] =
    useState(false);
  const [isActivityGroupDataAvailable, setIsActivityGroupDataAvailable] =
    useState(false);

  const stackOrder = [
    getTranslatedActivityGroupFromCamelCase(
      "buildingMoves",
      i18n.language
    ),
    getTranslatedActivityGroupFromCamelCase(
      "nonTransactional",
      i18n.language
    ),
    getTranslatedActivityGroupFromCamelCase(
      "transactional",
      i18n.language
    ),
    getTranslatedActivityGroupFromCamelCase(
      "project",
      i18n.language
    )
  ]

  useEffect(() => {
    setSelectedWorker(null);
  }, [employeesUnderManager]);

  useEffect(() => {
    setWorkerHoursLoading(true);
    const weekNumber = getWeekNumber(new Date());
    if (!isEmptyObject(selectedWorker)) {
      getEmployeeWeekData(selectedWorker.workerId, weekNumber).then(
        (querySnapshotData) => {
          const document = [];
          const uniqueActGroups = [];
          if (!isEmptyObject(querySnapshotData)) {
            if (querySnapshotData.timesheetStatus !== "deleted") {
              let jobFamilyTotals =
                querySnapshotData.weekTotalHours.jobFamilyTotals;
                console.log("jobFamilyTotals", querySnapshotData)

              let activityGroupTotals = {};

              for (let jobFamily in jobFamilyTotals) {
                const jobFamilyHours = jobFamilyTotals[jobFamily];
                activityGroupTotals = {};

                for (let activityGroup in jobFamilyHours) {
                  if (jobFamilyHours[activityGroup] !== 0) {
                    const translatedActivityGroup =
                      getTranslatedActivityGroupFromCamelCase(
                        activityGroup,
                        i18n.language
                      );
                    activityGroupTotals[translatedActivityGroup] = jobFamilyHours[activityGroup].toFixed(2);
                      !uniqueActGroups.includes(translatedActivityGroup) && uniqueActGroups.push(translatedActivityGroup);
                  }
                }

                document.push({
                  jobFamily: getTranslatedJobFamilyFromCamelCase(
                    jobFamily,
                    i18n.language
                  ) || _.startCase(jobFamily),
                  ...activityGroupTotals,
                });
              }
              console.log("setWorkerJobFamilyHours", document);

              const chartSeriesData = stackOrder.map((group) => ({
                name: group,
                data: document.map(item => item[group] || 0)
              }))

              setWorkerJobFamilyHours(document);
              setChartData(chartSeriesData);
              setIsJobFamilyDataAvailable(true);

              activityGroupTotals =
                querySnapshotData.weekTotalHours.activityGroupTotals;

              if (!isEmptyObject(activityGroupTotals)) {
                let totalHours = 0;

                for (let activityGroup in activityGroupTotals) {
                  if (activityGroupTotals[activityGroup] != 0) {
                    totalHours += activityGroupTotals[activityGroup];
                    const translatedActivityGroup =
                      getTranslatedActivityGroupFromCamelCase(
                        activityGroup,
                        i18n.language
                      );
                    activityGroupTotals[translatedActivityGroup] =
                      activityGroupTotals[activityGroup];
                  }
                }
                activityGroupTotals.totalHours = totalHours.toFixed(2);
              }

              setWorkerActivityGroupHours([activityGroupTotals]);
              setIsActivityGroupDataAvailable(true);
              if (querySnapshotData.weekTotalHours.activityHours === 0) {
                setIsActivityGroupDataAvailable(false);
                setIsJobFamilyDataAvailable(false);
              }
            } else {
              setIsJobFamilyDataAvailable(false);
              setIsActivityGroupDataAvailable(false);
              setWorkerJobFamilyHours([]);
              setWorkerActivityGroupHours([{}]);
            }
          } else {
            setIsJobFamilyDataAvailable(false);
            setIsActivityGroupDataAvailable(false);
            setWorkerJobFamilyHours([]);
            setWorkerActivityGroupHours([{}]);
          }
        }
      );
    } else {
      setIsJobFamilyDataAvailable(false);
      setIsActivityGroupDataAvailable(false);
      setWorkerJobFamilyHours([]);
      setWorkerActivityGroupHours([{}]);
    }
    setWorkerHoursLoading(false);
  }, [selectedWorker]);

  return (
    <>
      <LoaderView loading={employeesUnderManagerLoading}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={4}
        >
          <Grid item xs="auto" style={{ padding: "0px 16px 0px 16px" }}>
            <Autocomplete
              id="combo-box-demo"
              options={employeesUnderManager}
              getOptionLabel={(option) =>
                option.workerName + " (" + option.workerId + ") "
              }
              value={selectedWorker}
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setSelectedWorker(newValue);
                } else {
                  setSelectedWorker(null);
                }
              }}
              style={{ width: 200 }}
              renderInput={(params) => {
                if (selectedWorker === null) {
                  return <TextField {...params} label={t("other.lblSelectActivity")} />;
                } else {
                  return <TextField {...params} label={t("other.lblEmployee")} />;
                }
              }}
            />
          </Grid>

          {isActivityGroupDataAvailable ? (
            <>
            <Grid item xs="auto" style={{ padding: "0px 0px 0px 16px", display: 'flex' }}>
            <Typography variant="h6" style={{ marginRight: '10px' }}>
               {t("other.lblTotal")}
            </Typography>
            {/* <Typography variant="h6" style={{ marginLeft: '10px', color: 'green' }}>
              {workerActivityGroupHours[0]?.totalHours}
            </Typography> */}
            </Grid>
            <Grid item xs style={{ padding: "0px 16px 0px 0px" }}>
              <ApexCharts
                options={{
                  chart: {
                    type: "bar",
                    stacked: true,
                    height: '75',
                    toolbar : {
                      show : false
                    }
                  },
                   xaxis: {
                    categories: [
                      `  ${workerActivityGroupHours[0]?.totalHours}`
                      // getTranslatedActivityGroupFromCamelCase("buildingMoves", i18n.language),
                      // getTranslatedActivityGroupFromCamelCase("nonTransactional", i18n.language),
                      // getTranslatedActivityGroupFromCamelCase("transactional", i18n.language),
                      // getTranslatedActivityGroupFromCamelCase("project", i18n.language),
                    ],
                    // title : {
                    //   text : `${t("other.lblTotal")}`,
                    // },
                    axisBorder: {
                      show: false,  // Hide the axis line (border)
                    },
                    axisTicks: {
                      show: false,  // Hide ticks on the axis
                    },
                    labels: {
                      show: false
                    },
                  },
                   yaxis: {
                    axisBorder: {
                      show: false,  // Hide the axis line (border)
                    },
                    axisTicks: {
                      show: false,  // Hide ticks on the axis
                    },
                    labels: {
                      show: false,
                      style: {
                        fontSize: '1rem',
                        fontWeight: 700
                      },                    
                    },
                    title : {
                      text : `${workerActivityGroupHours[0]?.totalHours}`,
                      rotate: 0,
                      offsetY: 5,
                      style: {
                        fontSize : '0.975rem',
                        color: 'green'
                      }
                    },
                  },
                  tooltip: {
                    enabled: true,
                    y: {
                      formatter: (value) => `${value} hours`,
                    },
                  },
                  colors : ["#3C5772", "#00A88E", "#1B75BC", "#6DCFF6"], 
                  plotOptions: {
                    bar: {
                      borderRadius: 4,
                      borderRadiusApplication: 'end',
                      horizontal: true,
                      barHeight: "35",
                      dataLabels:{
                        // total:{
                        //   enabled: true,
                        //   style: {
                        //     fontSize: '1rem'
                        //   }
                        // },
                      }
                    }
                  },
                  legend: { 
                    show : false
                  },
                  grid: {
                    show : false
                  },
                }}
                series={stackOrder.map(item => ({
                  name: item,
                  data:[workerActivityGroupHours[0][item] || 0]
                }))}
                type = 'bar'
                height={workerActivityGroupHours.length * 65}
                width={'89%'}
              />
            </Grid>
            </>
          ) : null}


          <LoaderView loading={workerHoursLoading}>
            { isJobFamilyDataAvailable ? (
              <Grid item xs="auto" style={{ padding: "0px 16px 0px 16px" }}>
               <ApexCharts  
                  options={{
                    chart: {
                    type: 'bar',
                    height: 'auto',
                    stacked: true
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 4,
                      borderRadiusApplication: 'end',
                      horizontal: true,
                      barHeight:'35',
                      // dataLabels:{
                      //   total:{
                      //     enabled: true
                      //   }
                      // }
                    }
                  },
                  colors: ["#3C5772", "#00A88E", "#1B75BC", "#6DCFF6"], 
                  dataLabels: {
                    enabled: true,
                    // style:{
                    //   colors: ['#000'],
                    //   fontSize: '14px',
                    //   fontWeight: 700
                    // }
                  },
                  xaxis: {
                    categories: workerJobFamilyHours.length>0 ? workerJobFamilyHours.map((item) => item.jobFamily ? item.jobFamily : '') : [],
                    labels: {
                      style: {
                        fontSize : '14px'
                      }
                    },
                    axisBorder: {
                      show: true,  // Hide the axis line (border)
                    },
                  },
                  yaxis: {
                    labels: {
                      style: {
                        fontSize : '1rem'
                      }
                    },
                    axisBorder: {
                      show: true,  // Hide the axis line (border)
                    },
                  },
                  grid: {
                    show : true
                  },
                  }}
                  series={chartData}
                  type = 'bar'
                  height={(workerJobFamilyHours.length * 45)+100}
                  width={'99%'}
              />
              </Grid>
            ) : (
              <Box style={{ marginTop: "30px" , padding: '0 16px 0 16px'}} className={classes.full}>
                <Alert severity="info">{t("pgHome.errNoReportData2")}</Alert>
              </Box>
            )}
          </LoaderView>
        </Grid>
      </LoaderView>
    </>
  );
};

export default EmployeesActivities;
